export type ErrorCode = "CONFLICT" | "AUTH_INVALID_CREDENTIALS" | string;

interface ApiErrorPayload {
  code: ErrorCode;
  message: string;
  details?: {
    message?: string;
    [key: string]: unknown;
  };
}

interface ErrorResponse {
  success: false;
  error: ApiErrorPayload;
}

// Global error messages for unhandled errors
const globalErrorMessages: Record<ErrorCode, string> = {
  CONFLICT: "error.global.CONFLICT",
  AUTH_INVALID_CREDENTIALS: "error.global.AUTH_INVALID_CREDENTIALS",
  VALIDATION_ERROR: "error.global.VALIDATION_ERROR",
  ACCESS_DENIED: "error.global.ACCESS_DENIED",
};

// Specific error messages for known error cases
export const specificErrorMessages: Record<string, Record<ErrorCode, string>> = {
  createTruck: {
    CONFLICT: "error.createTruck.CONFLICT",
  },
  createUser: {
    CONFLICT: "error.createUser.CONFLICT",
  },
  createUangJalanReport: {
    RESOURCE_CONFLICT: "error.createUangJalanReport.RESOURCE_CONFLICT",
  },
  updateCompanySettings: {
    VALIDATION_ERROR: "companySettings.invalidLimit",
  },
};

const apiValidationMessageKeys: Record<string, string> = {
  "Missing required field: departureTimestamp": "orders.validation.departureRequired",
  "Missing required field: truckId": "orders.validation.truckRequired",
  "Missing required field: driverId": "orders.validation.driverRequired",
  "Missing required field: customerId": "orders.validation.customerRequired",
  "load must contain at least 1 city": "orders.validation.loadRequired",
  "unload must contain at least 1 city": "orders.validation.unloadRequired",
  "Missing required field: proofImage": "orders.validation.proofImageRequired",
  "Proof image object not found": "orders.proof.missingObject",
  "Proof image exceeds maximum allowed size": "orders.proof.tooLarge",
  "Invalid proof image key": "orders.proof.invalidKey",
  "Missing required field: externalOrderId": "orders.validation.externalOrderIdRequired",
  "Missing required field: expenses.uang_jalan": "orders.validation.expenseUangJalanRequired",
  "Missing required field: expenseUangJalan": "orders.validation.expenseUangJalanRequired",
  "expenseMobRoute must contain exactly 2 cities when mob amount is greater than 0":
    "orders.validation.expenseMobRouteRequired",
  "expenseMobRoute must contain exactly 2 cities when expenseMob is greater than 0":
    "orders.validation.expenseMobRouteRequired",
  "Invalid driverId": "orders.validation.invalidDriver",
  "driverId must reference a DRIVER user": "orders.validation.driverMustBeDriverRole",
  "Invalid customerId": "orders.validation.invalidCustomer",
  "Invalid truckId": "orders.validation.invalidTruck",
  "Order must be reopened to open status before editing": "orders.validation.mustReopenBeforeEdit",
  "Order in review can only transition to revision_required or verified":
    "orders.validation.inReviewCannotEdit",
  "dailyUangJalanReportLimit must be an integer between 1 and 100": "companySettings.invalidLimit",
};

type TranslationFunction = (key: string, options?: { [key: string]: string }) => string;

export const extractApiError = (error: unknown): ApiErrorPayload | undefined => {
  const candidate = error as {
    data?: { error?: ApiErrorPayload };
    response?: { data?: { error?: ApiErrorPayload } };
  };

  return candidate.response?.data?.error ?? candidate.data?.error;
};

const translateApiDetailMessage = (detailMessage: string, t: TranslationFunction): string => {
  const translationKey = apiValidationMessageKeys[detailMessage];
  if (translationKey) {
    return t(translationKey);
  }

  return detailMessage;
};

export const getErrorMessage = (error: any, t: TranslationFunction, context?: string): string => {
  const apiError = extractApiError(error);

  if (apiError?.code) {
    const errorCode = apiError.code;
    const detailMessage =
      typeof apiError.details?.message === "string" ? apiError.details.message : undefined;

    if (context && specificErrorMessages[context]?.[errorCode]) {
      return t(specificErrorMessages[context][errorCode]);
    }

    if (detailMessage) {
      return translateApiDetailMessage(detailMessage, t);
    }

    const globalMessage = globalErrorMessages[errorCode];
    if (globalMessage) {
      return t(globalMessage);
    }

    return t("error.global.UNKNOWN", { code: errorCode });
  }

  return t("error.global.DEFAULT");
};

export const isErrorResponse = (error: any): error is ErrorResponse => {
  const apiError = extractApiError(error);
  return Boolean(apiError?.code && apiError?.message);
};
